import { useState } from "react";

import { useHistory } from "react-router-dom";
import { CategoryStatus, ClusterDiscoveryStatus, ClusterType, EarlyAccessFeature } from "@doitintl/cmp-models";
import { Link, TableCell, Tooltip, Typography } from "@mui/material";
import { type AxiosError } from "axios";

import { useApiContext } from "../../../../api/context";
import { CellsWrapper } from "../../../../Components/FilterTable/Toolbar/CellsWrapper";
import useRouteMatchURL from "../../../../Components/hooks/useRouteMatchURL";
import { LimitedListWithTooltip } from "../../../../Components/LimitedListWithTooltip";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { getAxiosErrorMessage } from "../../../../utils/axios";
import { AWSFeatureName } from "../../../Settings/AWS/types";
import { getAwsCloudConnectSettingsLink } from "../../../Settings/AWS/utils";
import { getGcpCloudConnectSettingsLink } from "../../../Settings/GCP/utils";
import { activateCluster, connectEksCluster, deactivateCluster, deleteCluster, testClusterConnection } from "../../api";
import { CLUSTER_PAGE_MESSAGES } from "../../messages";
import { ClusterDialog } from "../ClusterDialog";
import AgentStatus from "./AgentStatus";
import ClusterAction from "./ClusterAction";
import { ClusterStatus } from "./ClusterStatus";
import ClusterThreeDotsMenu from "./ClusterThreeDotsMenu";
import ClusterTypeDisplay from "./ClusterType";
import DeleteClusterDialog from "./DeleteClusterDialog";
import { type ClusterTableItem } from "./types";

interface ClusterTableRowProps {
  row: ClusterTableItem;
}

const ClusterTableRow = ({ row }: ClusterTableRowProps) => {
  const routeMatchURL = useRouteMatchURL();
  const history = useHistory();
  const { isDoitEmployee } = useAuthContext();

  const { awsFeatures, customer } = useCustomerContext();
  const api = useApiContext();
  const errorSnackbar = useErrorSnackbar();
  const successSnackbar = useSuccessSnackbar();

  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const customerHasFsk8s = isDoitEmployee || customer.earlyAccessFeatures?.includes(EarlyAccessFeature.FSK8S);
  const canAccessK8SFeatures =
    isDoitEmployee || customer.earlyAccessFeatures?.includes(EarlyAccessFeature.K8S_FEATURES);

  const runClusterAction = async (action: (() => Promise<void>) | (() => void)) => {
    try {
      setActionLoading(true);
      await action();
    } catch (e) {
      const message = getAxiosErrorMessage(e as AxiosError);
      errorSnackbar(`Error performing action on cluster ${row.name}: ${message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const openEditDialog = () => {
    setEditDialogOpen(true);
  };

  const startEksClusterActivation = async () => {
    const hasFsk8sCore = awsFeatures?.includes(AWSFeatureName.fsk8s_core);
    const hasFsk8sAutoConnect = awsFeatures?.includes(AWSFeatureName.fsk8s_auto_connect);

    if (hasFsk8sCore) {
      if (hasFsk8sAutoConnect) {
        await runClusterAction(() => connectEksCluster(api, row.id));
      }
      history.push(`${routeMatchURL}/activate/eks?clusterId=${row.id}&projectId=${row.projectId}`);
    } else {
      history.push(
        getAwsCloudConnectSettingsLink({
          customer,
          cloudConnect: row.cloudConnect,
          callbackUrl: routeMatchURL,
        })
      );
    }
  };

  const activateGkeCluster = async () => {
    if (row.cloudConnect?.status === CategoryStatus.Healthy) {
      history.push(`${routeMatchURL}/activate/gke?clusterId=${row.id}&projectId=${row.projectId}`);
      return;
    }

    history.push(
      getGcpCloudConnectSettingsLink({
        customer,
        cloudConnect: row.cloudConnect,
        callbackUrl: routeMatchURL,
      })
    );
  };

  const handleTestConnection = async () => {
    await runClusterAction(() => testClusterConnection(api, row.id));
  };

  const handleEnableFeatures = async () => {
    if (!canAccessK8SFeatures) {
      return;
    }

    if (row.type === ClusterType.EKS) {
      await startEksClusterActivation();
      return;
    }

    if (row.type === ClusterType.GKE) {
      await activateGkeCluster();
      return;
    }

    if (!row.isActivated) {
      await runClusterAction(() => {
        activateCluster(api, row.id);
      });
    }
  };

  const handleDeactivateCluster = async () => {
    if (!canAccessK8SFeatures) {
      return;
    }

    await runClusterAction(() => deactivateCluster(api, row.id));
    successSnackbar(`Cluster deactivated successfully`);
  };

  const handleDeleteCluster = async () => {
    if (!canAccessK8SFeatures) {
      return;
    }

    setDeleteDialogOpen(false);
    await runClusterAction(() => deleteCluster(api, row.id));
    successSnackbar(`Cluster deleted successfully`);
  };

  const handleInstallAgent = () => {
    history.push(`${routeMatchURL}/${row.id}/agent/install`);
  };

  const handleManageAgent = () => {
    history.push(`${routeMatchURL}/${row.id}/agent/manage`);
  };

  const clusterCanBeActivated =
    !row.isPrivate && row.type !== ClusterType.SELF_MANAGED && row.discoveryStatus === ClusterDiscoveryStatus.HEALTHY;

  return (
    <>
      {deleteDialogOpen && (
        <DeleteClusterDialog
          cluster={row}
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
          onDelete={handleDeleteCluster}
        />
      )}
      {editDialogOpen && <ClusterDialog open={editDialogOpen} setOpen={setEditDialogOpen} cluster={row} />}
      <CellsWrapper>
        <TableCell>
          {clusterCanBeActivated && canAccessK8SFeatures ? (
            <Typography
              component={Link}
              onClick={handleEnableFeatures}
              color="inherit"
              variant="body2"
              title="Click to enable features"
              sx={{ textDecorationColor: "inherit", cursor: "pointer" }}
            >
              {row.name}
            </Typography>
          ) : (
            <Tooltip
              title={
                row.discoveryStatus === ClusterDiscoveryStatus.DELETED
                  ? CLUSTER_PAGE_MESSAGES.DELETED_CLUSTER
                  : undefined
              }
            >
              <Typography
                variant="body2"
                sx={{
                  opacity: row.discoveryStatus === ClusterDiscoveryStatus.DELETED ? "0.5" : "1",
                }}
              >
                {row.name}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{row.projectId || "N/A"}</TableCell>
        {canAccessK8SFeatures && (
          <TableCell>
            <ClusterStatus cluster={row} />
          </TableCell>
        )}
        <TableCell>
          <ClusterTypeDisplay type={row.type} />
        </TableCell>
        <TableCell>{row.region}</TableCell>
        <TableCell>{row.kubernetesControlPlaneVersion || "Unknown"}</TableCell>
        {customerHasFsk8s && (
          <TableCell>
            <AgentStatus cluster={row} />
          </TableCell>
        )}
        {canAccessK8SFeatures && (
          <TableCell>
            <LimitedListWithTooltip items={row.enabledFeatures?.split(", ") || []} limit={1} title="Enabled features" />
          </TableCell>
        )}
        <TableCell>
          {canAccessK8SFeatures ? (
            <ClusterThreeDotsMenu
              cluster={row}
              onTestConnection={handleTestConnection}
              onEnableFeatures={handleEnableFeatures}
              onDeactivateCluster={handleDeactivateCluster}
              onDeleteCluster={openDeleteDialog}
              onEditCluster={openEditDialog}
              onInstallAgent={handleInstallAgent}
              onManageAgent={handleManageAgent}
              loading={actionLoading}
            />
          ) : (
            <ClusterAction cluster={row} onInstallAgent={handleInstallAgent} onManageAgent={handleManageAgent} />
          )}
        </TableCell>
      </CellsWrapper>
    </>
  );
};

export default ClusterTableRow;
